import { useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart3, DollarSign, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '../../lib/apiClient';
import { usePortalPatientData } from './portalContext';
import { ErrorBanner, MetricCard, Panel, PortalPage, formatPortalCurrency } from './ui';

type PortalBillingResponse = {
  data?: Array<{
    id: string;
    date: string;
    total: number;
    balance: number;
  }>;
};

export default function ReportsPage() {
  const { t } = useTranslation();
  const { fetchJson } = useApiClient();
  const { buildPortalPath, refreshNonce, profile } = usePortalPatientData();
  const [rows, setRows] = useState<PortalBillingResponse['data']>([]);
  const [error, setError] = useState<string | null>(null);

  const loadReports = useCallback(async () => {
    try {
      setError(null);
      const response = await fetchJson<PortalBillingResponse>(buildPortalPath('/portal/billing?limit=100'));
      setRows(Array.isArray(response?.data) ? response.data : []);
    } catch (err: any) {
      setError(err?.message || t('reports.loadError'));
    }
  }, [buildPortalPath, fetchJson, t]);

  useEffect(() => {
    void loadReports();
  }, [loadReports, refreshNonce]);

  const summary = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const yearRows = (rows || []).filter((row) => new Date(row.date).getFullYear() === currentYear);
    const totalInvoiced = yearRows.reduce((sum, row) => sum + Number(row.total || 0), 0);
    const outstandingBalance = yearRows.reduce((sum, row) => sum + Number(row.balance || 0), 0);
    const totalPaid = totalInvoiced - outstandingBalance;
    return { totalInvoiced, totalPaid, outstandingBalance, invoiceCount: yearRows.length };
  }, [rows]);

  return (
    <PortalPage title={t('reports.title')} description={t('reports.description')}>
      {error ? <ErrorBanner message={error} /> : null}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard title={t('reports.totalInvoicedYtd')} value={formatPortalCurrency(summary.totalInvoiced)} icon={BarChart3} color="text-blue-600" />
        <MetricCard title={t('reports.totalPaidYtd')} value={formatPortalCurrency(summary.totalPaid)} icon={DollarSign} color="text-emerald-600" />
        <MetricCard title={t('reports.outstandingBalance')} value={formatPortalCurrency(summary.outstandingBalance)} icon={Wallet} color="text-rose-600" />
      </div>
      <Panel title={t('reports.reportNotes')}>
        <div className="space-y-2 text-sm text-slate-600">
          <p>
            <span className="font-semibold text-slate-700">{t('reports.patient')}</span> {profile?.firstName} {profile?.lastName}
          </p>
          <p>
            <span className="font-semibold text-slate-700">{t('reports.invoicesThisYear')}</span> {summary.invoiceCount}
          </p>
          <p>
            <span className="font-semibold text-slate-700">{t('reports.outstanding')}</span> {formatPortalCurrency(summary.outstandingBalance)}
          </p>
        </div>
      </Panel>
    </PortalPage>
  );
}
